import { useEffect } from "react";
import { useRouterState } from "@tanstack/react-router";
import { localeFromPathname } from "@/lib/analytics/cta-events";
import { trackFormAbandon, trackFormStart } from "@/lib/analytics/track";

/**
 * Global form funnel tracker.
 *
 * - `form_start`: the first time a visitor focuses or types in any form on the
 *   page (once per form).
 * - `form_abandon`: the visitor started a form but left the page, switched
 *   away for good, or navigated to another route without a successful
 *   submission - reported with the time spent in the form and how many fields
 *   were filled in.
 *
 * A successful submission clears the pending state; `trackFormSubmit()`
 * dispatches the `perceptica:form-submitted` event that this listens for.
 */

type PendingForm = {
  formName: string;
  pagePath: string;
  startedAt: number;
  element: HTMLFormElement;
};

const IGNORED_TYPES = new Set(["submit", "button", "reset", "hidden"]);

function resolveFormName(form: HTMLFormElement): string {
  const explicit = form.getAttribute("data-form-name") || form.getAttribute("name") || form.id;
  if (explicit) return explicit.slice(0, 120);
  const heading = form.closest("section, div")?.querySelector("h1, h2, h3")?.textContent;
  if (heading?.trim()) return heading.trim().slice(0, 120);
  return `form@${window.location.pathname}`.slice(0, 120);
}

function filledFieldCount(form: HTMLFormElement): number {
  let filled = 0;
  for (const el of Array.from(form.elements)) {
    const field = el as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    if (!("value" in field) || !field.name) continue;
    if ("type" in field && IGNORED_TYPES.has(field.type)) continue;
    if ("type" in field && (field.type === "checkbox" || field.type === "radio")) {
      if ((field as HTMLInputElement).checked) filled += 1;
      continue;
    }
    if (String(field.value ?? "").trim() !== "") filled += 1;
  }
  return filled;
}

export function FormEngagementTracker() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    const pending = new Map<HTMLFormElement, PendingForm>();

    function flush(reason: "unload" | "route_change") {
      for (const entry of pending.values()) {
        // Only report a genuine abandonment: the visitor typed something.
        const fields = filledFieldCount(entry.element);
        if (fields === 0 && reason === "route_change") continue;
        trackFormAbandon({
          form_name: entry.formName,
          time_in_form: Math.max(0, Math.round((Date.now() - entry.startedAt) / 1000)),
          fields_filled: fields,
          page_path: entry.pagePath,
          locale: localeFromPathname(entry.pagePath),
        });
      }
      pending.clear();
    }

    function onInteract(event: Event) {
      const target = event.target as HTMLElement | null;
      const form = target?.closest?.("form") as HTMLFormElement | null;
      if (!form || pending.has(form)) return;
      if (target && "type" in target && IGNORED_TYPES.has((target as HTMLInputElement).type)) return;

      const pagePath = window.location.pathname;
      const formName = resolveFormName(form);
      pending.set(form, { formName, pagePath, startedAt: Date.now(), element: form });
      trackFormStart({
        form_name: formName,
        page_path: pagePath,
        locale: localeFromPathname(pagePath),
      });
    }

    function onSubmitted(event: Event) {
      const name = (event as CustomEvent<{ form_name?: string }>).detail?.form_name;
      for (const [form, entry] of pending) {
        if (!name || entry.formName === name || form.contains(document.activeElement)) {
          pending.delete(form);
        }
      }
      if (!name) pending.clear();
    }

    function onPageHide() {
      flush("unload");
    }

    document.addEventListener("focusin", onInteract, true);
    document.addEventListener("input", onInteract, true);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("perceptica:form-submitted", onSubmitted as EventListener);

    return () => {
      flush("route_change");
      document.removeEventListener("focusin", onInteract, true);
      document.removeEventListener("input", onInteract, true);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("perceptica:form-submitted", onSubmitted as EventListener);
    };
  }, [pathname]);

  return null;
}
